import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult() {
  const { t } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id") || params.get("order_id");
  const isCancel = location.pathname.endsWith("/cancel");

  const [status, setStatus] = useState(isCancel ? "cancelled" : "pending");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled || n >= 6) {
        if (!cancelled && n >= 6) setStatus("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch (_) { /* keep polling */ }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, isCancel]);

  const renderBody = () => {
    if (status === "paid") {
      return (
        <>
          <CheckCircle2 className="w-16 h-16 text-[#22c55e] mb-4" />
          <h1 className="font-display text-3xl font-bold">{t("payment.success_title")}</h1>
          <p className="text-zinc-400 mt-2">{t("payment.success_subtitle")}</p>
        </>
      );
    }
    if (status === "cancelled" || status === "expired") {
      return (
        <>
          <XCircle className="w-16 h-16 text-red-500 mb-4" />
          <h1 className="font-display text-3xl font-bold">{t("payment.cancelled_title")}</h1>
          <p className="text-zinc-400 mt-2">{t("payment.cancelled_subtitle")}</p>
        </>
      );
    }
    return (
      <>
        <Loader2 className="w-16 h-16 text-[#FFD700] mb-4 animate-spin" />
        <h1 className="font-display text-3xl font-bold">{t("payment.verifying_title")}</h1>
        <p className="text-zinc-400 mt-2">{t("payment.verifying_subtitle", { n: attempts + 1 })}</p>
      </>
    );
  };

  return (
    <div className="max-w-md mx-auto py-10 text-center flex flex-col items-center" data-testid="payment-result">
      {renderBody()}
      <Link to="/my-rentals" className="mt-8 inline-flex items-center px-5 py-2.5 rounded-md bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="go-to-my-rentals">
        {t("payment.go_to_my_rentals")}
      </Link>
    </div>
  );
}
